import { CreateButton, Show, useModal } from "@refinedev/antd";
import { Button, Space, Tag, Row, Col } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  BankOutlined,
  MailOutlined,
  FileProtectOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  AuditOutlined,
} from "@ant-design/icons";

import { IShipco } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
  ShowModalItem,
} from "components/show";

export const ShipcoShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IShipco>();
  const record = result;

  const {
    modalProps: modalPropsLicence,
    show: showLicence,
    close: closeLicence,
  } = useModal();
  const {
    modalProps: modalPropsContacts,
    show: showContacts,
    close: closeContacts,
  } = useModal();
  const {
    modalProps: modalPropsAddress,
    show: showAddress,
    close: closeAddress,
  } = useModal();

  const hasLicences = Array.isArray(record?.licences) && (record?.licences?.length ?? 0) > 0;
  const hasContacts = Array.isArray(record?.contacts) && (record?.contacts?.length ?? 0) > 0;
  const hasAddress = !!record?.address;

  return (
    <Show
      breadcrumb={false}
      isLoading={isLoading}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active shipping company",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      footerButtons={
        <Space>
          {!hasLicences ? (
            <CreateButton resource="licence">Setup Licence</CreateButton>
          ) : (
            <Button type="default" onClick={showLicence}>
              Associated Licences
            </Button>
          )}
          {!hasContacts ? (
            <CreateButton resource="contact">Setup Contact</CreateButton>
          ) : (
            <Button type="default" onClick={showContacts}>
              Associated Contacts
            </Button>
          )}
          {hasAddress ? (
            <Button type="default" onClick={showAddress}>
              Shipco Address
            </Button>
          ) : (
            <CreateButton resource="address">Setup Address</CreateButton>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Company Registration */}
        <ShowSection
          title="Company Registration"
          subtitle="Legal business registration details"
          icon={<BankOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Registration Name" value={record?.registrationName} />
            <ShowField label="Registration Number" value={record?.registrationNumber} type="code" copyable />
            <ShowField label="GST Number" value={record?.gstNumber} type="code" copyable />
            <ShowField label="Accreditation Number" value={record?.accreditationNumber} type="code" copyable />
          </ShowFieldGrid>
        </ShowSection>

        {/* Contact Information */}
        <ShowSection
          title="Contact Information"
          subtitle="Primary communication channels"
          icon={<MailOutlined />}
          variant="default"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Phone" value={record?.phone} type="phone" />
            <ShowField label="Communication Email" value={record?.email} type="email" />
          </ShowFieldGrid>
        </ShowSection>
      </Space>

      {/* Licences Modal */}
      <ShowModal
        title="Associated Licences"
        subtitle={record?.registrationName}
        icon={<FileProtectOutlined />}
        open={modalPropsLicence.open ?? false}
        onClose={closeLicence}
        empty={!hasLicences}
        emptyText="No licences configured for this shipping company"
        width={700}
      >
        {record?.licences?.map((licence, index) => (
          <ShowModalItem
            key={index}
            title={licence?.vessel?.name || `Licence ${index + 1}`}
            showDivider={index < (record?.licences?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="UKHO Licence" value={licence?.ukhoLicenceId} type="code" copyable />
              <ShowField label="Vessel Name" value={licence?.vessel?.name} />
              <ShowField label="Vessel Type" value={licence?.vessel?.type} type="tag" tagColor="blue" />
              <ShowField label="IMO Number" value={licence?.vessel?.imoNumber} type="code" />
              <ShowField label="Call Sign" value={licence?.vessel?.callSign} type="code" />
              <ShowField label="Status" value={licence?.vessel?.holdStatus} type="status" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Contacts Modal */}
      <ShowModal
        title="Associated Contacts"
        subtitle={record?.registrationName}
        icon={<TeamOutlined />}
        open={modalPropsContacts.open ?? false}
        onClose={closeContacts}
        empty={!hasContacts}
        emptyText="No contacts configured for this shipping company"
      >
        {record?.contacts?.map((contact, index) => (
          <ShowModalItem
            key={index}
            title={`Contact ${index + 1}`}
            showDivider={index < (record?.contacts?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="Name" value={contact?.name} />
              <ShowField label="Email" value={contact?.email} type="email" />
              <ShowField label="Phone" value={contact?.phone} type="phone" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Address Modal */}
      <ShowModal
        title="Shipco Address"
        subtitle={record?.registrationName}
        icon={<EnvironmentOutlined />}
        open={modalPropsAddress.open ?? false}
        onClose={closeAddress}
        empty={!hasAddress}
        emptyText="No address configured for this shipping company"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Address Line 1" value={record?.address?.addressLine1} />
          <ShowField label="Address Line 2" value={record?.address?.addressLine2} />
          <ShowField label="Address Line 3" value={record?.address?.addressLine3} />
          <ShowField label="City" value={record?.address?.city} />
          <ShowField label="State" value={record?.address?.state} />
          <ShowField label="Country" value={record?.address?.country?.name} />
          <ShowField label="Postal Code" value={record?.address?.postalCode} type="code" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
